import { FC, ReactNode } from 'react';

import {
  trackDateInputUsageAnalyticsEvent,
  trackDropdownInputUsageAnalyticsEvent,
  trackOpenTextInputUsageAnalyticsEvent
} from 'analytics/events/input-usage';

import { observer } from 'mobx-react';

import { useFormContext } from 'react-hook-form';
import { Col, Row } from 'reactstrap';
import { sharedTestSelectors } from 'tests/shared/shared.selectors';

import { defaultMinDateInput, parseDateForInputField } from 'utils/DateUtils';
import * as ValidationUtils from 'utils/ValidationUtils';

import { RHFStyledInput } from 'views/Widgets/StyledInput';

import { ExclusiveToggleOption } from 'components/UIkit/atoms/Button/Toggle/ExclusiveToggleButton';

import { FormAutocomplete } from 'components/UIkit/atoms/Dropdown';

import './EditInfoInputs.scss';

interface EditInfoInputsProps {
  disableInputs: boolean;
  isExistingPatient: boolean;
  isOptOutFlow: boolean; // TODO rename to something relevant
  isUninvitedFlow?: boolean; // TODO rename to something relevant
  lastInput?: ReactNode;
}

export const sexOptions = [
  {
    label: 'Male',
    value: 1
  },
  {
    label: 'Female',
    value: 2
  },
  {
    label: 'Other',
    value: 3
  }
];

export const enrolmentStatuses: ExclusiveToggleOption[] = [
  {
    testHook: 'enrollment-status-chemo-teach',
    value: 'CHEMO_TEACH',
    label: 'Chemo Teach'
  },
  {
    testHook: 'enrollment-status-established',
    value: 'ESTABLISHED',
    label: 'Established'
  }
];

export const localeOptions = [
  {
    label: 'English',
    value: 'en_US'
  },
  {
    label: 'Spanish',
    value: 'es_US'
  }
];

const EditInfoInputs: FC<EditInfoInputsProps> = ({
  disableInputs,
  isExistingPatient,
  isOptOutFlow,
  isUninvitedFlow,
  lastInput
}) => {
  const { register, formState, getValues, watch } = useFormContext();
  const mrnDisabled = disableInputs || isExistingPatient;
  const { errors } = formState;

  const [dateOfBirth] = watch(['dateOfBirth']);

  return (
    <div className="edit-info-inputs">
      <Row className="mb-3">
        <Col xs={12} sm={6} lg={6} data-test-hook="first-name">
          <RHFStyledInput
            label="First Name"
            name="firstName"
            isRequired
            validate={(v: string) => ValidationUtils.isValidName(v) && !!v.trim()}
            disabled={disableInputs}
            register={register}
            error={Boolean(errors.firstName)}
            onBlur={(_, currentValue, valueAfterFocus) =>
              trackOpenTextInputUsageAnalyticsEvent(currentValue, valueAfterFocus, 'First Name')
            }
            getValues={getValues}
            testHook={sharedTestSelectors.firstNameInput}
          />
        </Col>
        <Col xs={12} sm={6} lg={6} data-test-hook="last-name">
          <RHFStyledInput
            label="Last Name"
            name="lastName"
            register={register}
            isRequired
            disabled={disableInputs}
            validate={(v: string) => ValidationUtils.isValidName(v) && !!v.trim()}
            error={Boolean(errors.lastName)}
            onBlur={(_, currentValue, valueAfterFocus) =>
              trackOpenTextInputUsageAnalyticsEvent(currentValue, valueAfterFocus, 'Last Name')
            }
            getValues={getValues}
            testHook="last-name-input"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={12} sm={6} lg={6}>
          <div data-test-hook="mrn">
            <RHFStyledInput
              label="Medical Record Number"
              name="mrn"
              type="text"
              register={register}
              disabled={mrnDisabled}
              isRequired={!mrnDisabled}
              validate={(v: string) => mrnDisabled || ValidationUtils.isValidMrnNumber(v)}
              readOnly={mrnDisabled}
              error={Boolean(errors.mrn)}
              onBlur={(_, currentValue, valueAfterFocus) =>
                trackOpenTextInputUsageAnalyticsEvent(
                  currentValue,
                  valueAfterFocus,
                  'Medical Record Number'
                )
              }
              getValues={getValues}
              testHook="mrn-input"
            />
          </div>
        </Col>
        <Col xs={12} sm={6} lg={6} data-test-hook="date-of-birth">
          <RHFStyledInput
            type="date"
            label="Date of Birth"
            register={register}
            name="dateOfBirth"
            disabled={disableInputs}
            isRequired
            max={parseDateForInputField(new Date())}
            min={defaultMinDateInput}
            error={Boolean(errors.dateOfBirth)}
            onChange={(event, currentValue) => {
              trackDateInputUsageAnalyticsEvent(currentValue, dateOfBirth, 'Date of Birth');
            }}
            getValues={getValues}
            testHook="dob-input"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={12} sm={6} lg={6} data-test-hook="sex">
          <FormAutocomplete
            label="Sex"
            name="sex"
            isRequired={!isOptOutFlow && !isUninvitedFlow}
            isClearable={false}
            isDisabled={disableInputs}
            options={sexOptions}
            onChange={(_, actionMeta, eventKey) => {
              trackDropdownInputUsageAnalyticsEvent(actionMeta, 'Sex', eventKey === 'Enter');
            }}
            testHook="sex-select"
          />
        </Col>
        <Col xs={12} sm={6} lg={6} data-test-hook="language">
          <FormAutocomplete
            label="Language Preference"
            isDisabled={disableInputs}
            isClearable={false}
            name="language"
            options={localeOptions}
            onChange={(_, actionMeta, eventKey) => {
              trackDropdownInputUsageAnalyticsEvent(actionMeta, 'Language', eventKey === 'Enter');
            }}
            testHook="language-select"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={12} sm={6} lg={6}>
          <RHFStyledInput
            type="email"
            label="Email"
            register={register}
            validate={(v: string) => (v?.length ? ValidationUtils.isEmail(v) : true)}
            name="email"
            error={Boolean(errors.email)}
            onBlur={(_, currentValue, valueAfterFocus) =>
              trackOpenTextInputUsageAnalyticsEvent(currentValue, valueAfterFocus, 'Email')
            }
          />
        </Col>
        {lastInput && (
          <Col xs={12} sm={6} lg={6}>
            {lastInput}
          </Col>
        )}
      </Row>
    </div>
  );
};

export default observer(EditInfoInputs);
